/**
 * C4 · Reference-tone synthesis: renders an exercise's note timeline with a
 * soft triangle-wave voice plus a count-in, scheduled sample-accurately on
 * the WebAudio clock so the visual target, audio and scoring stay aligned.
 */

import { midiToFreq, type RenderedExercise } from '@solfo/shared';

let ctx: AudioContext | null = null;

export function audioContext(): AudioContext {
  if (!ctx) ctx = new AudioContext();
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

export interface PlaybackHandle {
  /** AudioContext time at which beat 1 of the exercise starts (after count-in). */
  startTime: number;
  countInSec: number;
  stop: () => void;
}

export function playClick(when: number, accent = false) {
  const ac = audioContext();
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.frequency.value = accent ? 1400 : 1000;
  gain.gain.setValueAtTime(0.4, when);
  gain.gain.exponentialRampToValueAtTime(0.001, when + 0.07);
  osc.connect(gain).connect(ac.destination);
  osc.start(when);
  osc.stop(when + 0.08);
}

export function playExercise(
  rendered: RenderedExercise,
  opts: { tempoBpm: number; countInBeats?: number; gainDb?: number; referenceOnly?: boolean } ,
): PlaybackHandle {
  const ac = audioContext();
  const countInBeats = opts.countInBeats ?? 4;
  const secPerBeat = 60 / opts.tempoBpm;
  const countInSec = countInBeats * secPerBeat;
  const t0 = ac.currentTime + 0.15;
  const start = t0 + countInSec;

  for (let b = 0; b < countInBeats; b++) playClick(t0 + b * secPerBeat, b === 0);

  const master = ac.createGain();
  master.gain.value = Math.pow(10, (opts.gainDb ?? -8) / 20);
  master.connect(ac.destination);

  const nodes: (OscillatorNode | GainNode)[] = [master];
  for (const note of rendered.notes) {
    if (note.midi === null) continue;
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.type = 'triangle';
    const nStart = start + note.startSec;
    const nEnd = nStart + note.durSec;
    osc.frequency.setValueAtTime(midiToFreq(note.midi), nStart);
    if (note.glideToMidi !== null) {
      osc.frequency.linearRampToValueAtTime(midiToFreq(note.glideToMidi), nEnd);
    }
    gain.gain.setValueAtTime(0.0001, nStart);
    gain.gain.exponentialRampToValueAtTime(1, nStart + 0.02);
    gain.gain.setValueAtTime(1, Math.max(nStart + 0.02, nEnd - 0.03));
    gain.gain.exponentialRampToValueAtTime(0.0001, nEnd);
    osc.connect(gain).connect(master);
    osc.start(nStart);
    osc.stop(nEnd + 0.01);
    nodes.push(osc, gain);
  }

  return {
    startTime: start,
    countInSec,
    stop: () => {
      for (const n of nodes) {
        try {
          if (n instanceof OscillatorNode) n.stop();
          n.disconnect();
        } catch {
          /* already stopped */
        }
      }
    },
  };
}

/** Single sustained reference tone (range test, tuning). */
export function playTone(midi: number, durSec = 1.2): void {
  const ac = audioContext();
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = 'triangle';
  osc.frequency.value = midiToFreq(midi);
  const t = ac.currentTime;
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(0.3, t + 0.03);
  gain.gain.setValueAtTime(0.3, t + durSec - 0.15);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + durSec);
  osc.connect(gain).connect(ac.destination);
  osc.start(t);
  osc.stop(t + durSec + 0.02);
}
